import { COLORS, FONTS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';
import { AppIcon } from '../AppIcon';
import { IconUploader } from './IconUploader';

interface DeployedAppMetaEditorProps {
  /** Current display name of the app, used for the icon fallback avatar. */
  appName: string;
  /** Stored icon URL, shown until the operator picks a replacement. */
  iconUrl: string;
  name: string;
  description: string;
  category: string;
  team: string;
  /** Newly picked logo, or null to keep the stored one. */
  icon: File | null;
  onNameChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onCategoryChange: (v: string) => void;
  onTeamChange: (v: string) => void;
  onIconChange: (f: File | null) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  isDark: boolean;
  colors: ColorTheme;
  blueBtnStyle: React.CSSProperties;
  outlineBtnStyle: React.CSSProperties;
}

/**
 * Inline metadata editor for a pipeline-deployed app: display name, description,
 * category, team and logo. State lives in the parent so a save can refresh the
 * app record; this component is presentation only.
 */
export function DeployedAppMetaEditor(props: DeployedAppMetaEditorProps): React.JSX.Element {
  const { colors, isDark } = props;
  return (
    <div style={{
      padding: '16px', borderRadius: '10px', border: `1px solid ${colors.border}`,
      backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card, marginBottom: '20px',
      maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '10px',
    }}>
      <EditField label="Name" value={props.name} onChange={props.onNameChange} colors={colors} isDark={isDark} />
      <EditField label="Description" value={props.description} onChange={props.onDescriptionChange} colors={colors} isDark={isDark} />
      <EditField label="Category" value={props.category} onChange={props.onCategoryChange} colors={colors} isDark={isDark} />
      <EditField label="Team" value={props.team} onChange={props.onTeamChange} colors={colors} isDark={isDark} />
      <div>
        <div style={labelStyle(colors)}>Logo</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {!props.icon && <AppIcon icon={props.iconUrl} name={props.appName} isDark={isDark} size={40} />}
          <IconUploader
            isDark={isDark}
            colors={{ text: colors.text, textSecondary: colors.textSecondary, border: colors.border, card: colors.card }}
            onChange={props.onIconChange}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <button onClick={props.onSave} disabled={props.saving} style={{ ...props.blueBtnStyle, opacity: props.saving ? 0.5 : 1 }}>
          {props.saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={props.onCancel} disabled={props.saving} style={props.outlineBtnStyle}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Shared uppercase micro-label above each field. */
function labelStyle(colors: ColorTheme): React.CSSProperties {
  return {
    fontFamily: FONTS.heading, fontSize: '9px', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: colors.textSecondary, marginBottom: '4px',
  };
}

/** Labeled text input used by the metadata edit panel. */
function EditField({ label, value, onChange, colors, isDark }: {
  label: string; value: string; onChange: (v: string) => void;
  colors: ColorTheme; isDark: boolean;
}): React.JSX.Element {
  return (
    <div>
      <div style={labelStyle(colors)}>{label}</div>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', height: '36px', padding: '0 12px', borderRadius: '6px',
          border: `1px solid ${colors.border}`,
          backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
          color: colors.text, fontFamily: FONTS.body, fontSize: '13px', outline: 'none',
        }}
      />
    </div>
  );
}
